import React from 'react'
import { useIntl } from 'react-intl'
import FormattedDuration from 'react-intl-formatted-duration'
import { useRecoilValue } from 'recoil'
import { notifyParent } from 'mailup-react-helpers'
import { useConditions, useStorage } from 'Hooks/'

import {
  STORE_KEY_ACCOUNT,
  SEND_METHOD_TYPE_FREQUENCY,
  STORE_KEY_LISTS_COUNT
} from 'Constants'

import {
  Title,
  TITLE_VARIANT_L,
  Text,
  TEXT_VARIANT_BOLD,
  Flex,
  Grid,
  GRID_VARIANT_COLLAPSED,
  GridCell,
  HelpIcon,
  Link,
  LINK_VARIANT_HIGHLIGHT,
  Divider,
  Button,
  SPACING_16,
  SpacingVertical
} from 'mailup-web-components'
import styled from '@emotion/styled'

import NoItems from '../components/NoItems/'
import IconCell from '../components/Table/IconCell'
import MessageCell from '../components/Table/MessageCell'
import ListCell from '../components/Table/ListCell'
import RecipientsCell from '../components/Table/RecipientsCell'
import DateCell from '../components/Table/DateCell'
import PhaseCell from '../components/Table/PhaseCell'
import ActionsCell from '../components/Table/ActionsCell'
import Head from '../components/Table/Head'

import { listSendingState } from '../recoil/state/page'

export const ListSending = () => {
  const intl = useIntl()
  const { Items: items, TotalSendTime: totalSendTime } = useRecoilValue(
    listSendingState
  )

  const [hasSmtpPlusUsers, showLinkECommerce] = useConditions([
    'c:SmtpPlusOrFastEmailUsersPresent',
    'NOT c:IsRetailer AND NOT c:IsDeveloper AND uo:GEST AND NOT co:TURNOFFMSHOP'
  ])

  const [{ SendMethodType: sendMethodType }, listsCount] = useStorage([
    STORE_KEY_ACCOUNT,
    STORE_KEY_LISTS_COUNT
  ]).values

  const handleClickBuy = () =>
    notifyParent('redirectMainFrame', ['mshop_Redirector.aspx?t=mshopem'])

  return (
    <>
      <StyledSection data-mu-test='b-sendstatus-list--sending'>
        <Flex justifyContent='center' margin='0 0 16px  0'>
          <Title variant={TITLE_VARIANT_L}>
            {intl.formatMessage({ id: 'Load.Schedulation.InvioCorsoTitle' })}
          </Title>
        </Flex>

        {!!items.length && (
          <>
            <Head lastColumnStringId={'Load.Schedulation.TableStartDate'} />
            {items.map((item, i) => (
              <>
                <GridWithProgress
                  progress={item.progress}
                  variant={GRID_VARIANT_COLLAPSED}
                  data-mu-test='sending__results-table__row'
                  key={`list-sending-item-${i}-${item.idSending}`}
                >
                  <GridCell span={1}>
                    <IconCell {...item} withAnimation />
                  </GridCell>
                  <GridCell span={4}>
                    <MessageCell {...item} />
                  </GridCell>
                  <GridCell span={1}>
                    {listsCount && <ListCell {...item} />}
                  </GridCell>
                  <GridCell span={2}>
                    <RecipientsCell {...item} />
                  </GridCell>
                  <GridCell span={2}>
                    <DateCell {...item} />
                  </GridCell>
                  <GridCell span={1}>
                    <PhaseCell {...item} withAnimation />
                  </GridCell>
                  <GridCell span={1}>
                    <ActionsCell {...item} origin='list-sending' />
                  </GridCell>
                </GridWithProgress>
                <Divider />
                {/* {i === items.length - 1 && <Divider />} */}
              </>
            ))}
            <Flex
              alignItems='center'
              margin='24px 0 0 0'
              flexDirection='column'
            >
              <Text>
                <Text variant={TEXT_VARIANT_BOLD} htmlElement='span'>
                  {intl.formatMessage({ id: 'Load.Schedulation.TempoStimato' })}{' '}
                  <FormattedDuration
                    seconds={totalSendTime}
                    unitDisplay='narrow'
                    format='{days} {hours} {minutes} {seconds}'
                  />
                </Text>{' '}
                {intl.formatMessage({
                  id: 'Load.Schedulation.TempoStimatoTesto'
                })}
                {hasSmtpPlusUsers && (
                  <HelpIcon
                    text={intl.formatMessage({
                      id: 'Load.Schedulation.TempoStimatoTooltip'
                    })}
                  />
                )}
              </Text>
              {showLinkECommerce && (
                <>
                  <Text>
                    {intl.formatMessage({
                      id: 'Load.Schedulation.TempoStimatoUpsellingFrequenzaVer2'
                    })}
                  </Text>
                  <SpacingVertical variant={SPACING_16} />
                  <Button
                    kind='secondary'
                    onClick={handleClickBuy}
                    data-mu-test='btn-increase-your-sending-speed'
                  >
                    {intl.formatMessage({
                      id: 'Load.Queue.TempoStimatoUpsellingBandaIntl'
                    })}
                  </Button>
                </>
              )}
            </Flex>
          </>
        )}

        {!items.length && (
          <NoItems>
            {intl.formatMessage({ id: 'Load.Schedulation.InvioCorsoNoData' })}
          </NoItems>
        )}
      </StyledSection>
    </>
  )
}

const GridWithProgress = styled(Grid)`
  border-left: 1px solid #d5dadf;
  border-right: 1px solid #d5dadf;
  background-color: white;
  background-image: linear-gradient(
    to right,
    #d5eec7 0%,
    #d5eec7 100%
  ) !important;
  background-repeat: no-repeat !important;
  transition: background-size 1s ease-in-out;
  background-size: 0% 100%;
  background-size: ${props =>
    props.progress >= 0
      ? `${props.progress * 100}% 100% !important`
      : `0% 100% !important`};
`

const StyledSection = styled.section`
  .c-sendstatus-item__type {
    color: var(--mantis);
  }

  .with-animation {
    animation: flash 2s ease infinite;

    @keyframes flash {
      0%,
      100%,
      50% {
        opacity: 1;
      }
      25%,
      75% {
        opacity: 0;
      }
    }
  }
`
